import { Injectable, OnDestroy } from '@angular/core';
import { SessionService } from '@nano/core/session';
import { IdleService } from '@nano/core/idle';
import { AuthService } from '@nano/core/auth';
import { ApiEndpoints, ApiJwtResponse, ApiService } from '@nano/core/api';
import { of } from 'rxjs';
import { take, filter, catchError, map, switchMap } from 'rxjs/operators';
import { L10nTranslationService } from 'angular-l10n';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

type BoldDeskDayPeriod = 'DAY' | 'NIGHT';
type BoldDeskJwtPayload = {
  jwt?: string;
  email?: string;
};
type BoldDeskJwtResponse = ApiJwtResponse & BoldDeskJwtPayload;
type BoldDeskIdentityPayload = {
  email: string;
  jwt: string;
  name: string;
  partyId: string;
};
type BoldChatCommand = [string, ...unknown[]];
type BoldChatQueue = BoldChatCommand[];
type BoldChatApi = {
  push: (command: BoldChatCommand) => unknown;
};

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class BolddeskService implements OnDestroy {
  private loaded = false;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private launcherBtn: HTMLElement | null = null;
  private widgetUrl: string | null = null;
  private lastIdentityKey: string | null = null;
  private needsSessionReset = false;
  private readonly CHAT_SCRIPT_ID = 'boldchat-widget-script';

  private readonly CHAT_HEARTBEAT_INTERVAL_MS = 120000; // 2 minutes

  private lastTypingTime: number | null = null;
  private maxNighttimeSessionDurationMs = 25 * 60 * 1000; // 25 minutes
  private nighttimeSessionStartTime: number | null = null;

  constructor(
    private sessionService: SessionService,
    private idleService: IdleService,
    private authService: AuthService,
    private apiService: ApiService,
    private translationService: L10nTranslationService
  ) {}

  loadChatWidget(): void {
    if (this.loaded) return;
    const isDaytime = this.isDaytime();
    const dayPeriod: BoldDeskDayPeriod = isDaytime ? 'DAY' : 'NIGHT';
    const daytimeUrl = this.translationService.translate('bolddesk.daytimeWidgetUrl') as string;
    const nighttimeUrl = this.translationService.translate('bolddesk.nighttimeWidgetUrl') as string;
    this.widgetUrl = isDaytime ? daytimeUrl : nighttimeUrl;

    // BoldDesk requires settings to be defined before the widget script is loaded.
    this.ensureBoldChatSettings();
    this.ensureBoldChatQueue();

    this.authService.userProfile$
      .pipe(
        untilDestroyed(this),
        switchMap((userProfile) => {
          if (!userProfile) {
            this.clearIdentity();
            return of(null);
          }
          return this.apiService.get<BoldDeskJwtResponse>(ApiEndpoints.boldDeskJwt(dayPeriod)).pipe(
            map((response) => {
              const nestedCandidate = response?.response ?? response?.result;
              const nested = nestedCandidate && typeof nestedCandidate === 'object' ? (nestedCandidate as BoldDeskJwtPayload) : undefined;

              const email = typeof response?.email === 'string' ? response.email : typeof nested?.email === 'string' ? nested.email : undefined;
              const jwt = typeof response?.jwt === 'string' ? response.jwt : typeof nested?.jwt === 'string' ? nested.jwt : undefined;

              if (!email || !jwt) {
                return null;
              }

              return {
                email,
                jwt,
                name: userProfile.fullName ?? '',
                partyId: userProfile.partyId ?? ''
              };
            }),
            catchError(() => of(null))
          );
        }),
        filter((payload): payload is BoldDeskIdentityPayload => !!payload)
      )
      .subscribe(({ email, jwt, name, partyId }) => {
        this.applyIdentity(email, jwt, name, partyId);
        this.initializeWidgetIfNeeded();
      });
  }

  private appendScript(url: string): void {
    document.getElementById(this.CHAT_SCRIPT_ID)?.remove();

    const script = document.createElement('script');
    script.id = this.CHAT_SCRIPT_ID;
    script.src = url;
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);
    this.loaded = true;
  }

  private initializeWidgetIfNeeded(): void {
    if (this.loaded || !this.widgetUrl) return;

    this.appendScript(this.widgetUrl);
    this.waitForChatLauncher((launcherBtn) => {
      this.launcherBtn = launcherBtn;
      this.monitorChatState(launcherBtn);
      this.observeWidgetState();
    });
  }

  applyIdentity(email: string, jwt: string, name?: string, partyId?: string): void {
    if (!jwt || !email) {
      return;
    }

    this.ensureBoldChatQueue();

    const identityKey = partyId || `${email}|${jwt}|${name ?? ''}`;
    const identityChanged = !!this.lastIdentityKey && this.lastIdentityKey !== identityKey;
    if (!this.needsSessionReset && this.lastIdentityKey === identityKey) {
      return;
    }

    const settings: Record<string, string> = {
      email,
      userToken: jwt
    };
    if (name) {
      settings.name = name;
    }
    window['boldChatSettings'] = settings;
    if (this.needsSessionReset || identityChanged) {
      this.resetWidgetRuntime();
      this.resetChatSession();
    }
    this.lastIdentityKey = identityKey;
    this.needsSessionReset = false;
    this.initializeWidgetIfNeeded();
  }

  clearIdentity(): void {
    this.resetWidgetRuntime();

    window['boldChatSettings'] = {
      email: '',
      name: '',
      userToken: ''
    };
    this.resetChatSession();
    this.lastIdentityKey = null;
    this.needsSessionReset = true;
  }

  private convertToLithuaniaTime(date: Date): Date {
    return new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Vilnius' }));
  }

  private getShadowRoot(): ShadowRoot | null {
    const host = document.querySelector('#boldchat-host');
    return host instanceof HTMLElement ? host.shadowRoot : null;
  }

  private ensureBoldChatSettings(): void {
    const settings = this.getBoldChatSettings();
    const email = typeof settings.email === 'string' ? settings.email : '';
    const name = typeof settings.name === 'string' ? settings.name : '';
    const userToken = typeof settings.userToken === 'string' ? settings.userToken : '';
    window['boldChatSettings'] = { email, name, userToken };
  }

  private getBoldChatSettings(): Record<string, unknown> {
    const boldChatSettings = window['boldChatSettings'] as unknown;
    if (boldChatSettings && typeof boldChatSettings === 'object') {
      return boldChatSettings as Record<string, unknown>;
    }
    return {};
  }

  private resetBoldChatQueue(): BoldChatQueue {
    window['$boldChat'] = [];
    return window['$boldChat'] as BoldChatQueue;
  }

  private ensureBoldChatQueue(): BoldChatQueue | BoldChatApi {
    const boldChat = window['$boldChat'] as unknown;
    if (Array.isArray(boldChat)) {
      return boldChat as BoldChatQueue;
    }
    if (boldChat && typeof (boldChat as BoldChatApi).push === 'function') {
      return boldChat as BoldChatApi;
    }
    return this.resetBoldChatQueue();
  }

  private queueBoldChatCommand(command: BoldChatCommand): void {
    this.ensureBoldChatQueue().push(command);
  }

  private resetChatSession(): void {
    // BoldDesk stores session in cookie/localStorage; clear to avoid stale unverified conversation.
    this.queueBoldChatCommand(['do:clearSession']);
    this.queueBoldChatCommand(['do:setIsOpen', false]);
  }

  private resetWidgetRuntime(): void {
    this.stopChatHeartbeat();
    this.resetBoldChatQueue();
    if (this.loaded) {
      this.destroyWidget();
      this.loaded = false;
    }
  }

  private destroyWidget(): void {
    if (this.launcherBtn) {
      this.launcherBtn.removeEventListener('click', this.handleLauncherClick);
      this.launcherBtn = null;
    }

    document.querySelector('#boldchat-host')?.remove();
    document.querySelector('#feedback-form-widget-icon')?.remove();
    document.querySelector('#widget_popup_iframe_parent_element')?.remove();
    document.getElementById(this.CHAT_SCRIPT_ID)?.remove();
  }

  private getWidgetElement(): HTMLElement | null {
    const shadowRoot = this.getShadowRoot();
    const widget = shadowRoot?.querySelector('#bc-widget');
    return widget instanceof HTMLElement ? widget : null;
  }

  private isWidgetState(state: 'open' | 'closed'): boolean {
    const widget = this.getWidgetElement();
    if (!widget) {
      return false;
    }

    const classList = widget.classList;
    const isOpen = classList.contains('bc-show') && !classList.contains('bc-hide') && !classList.contains('bc-wid-animation-hide');
    return state === 'open' ? isOpen : !isOpen;
  }

  isBoldDeskOpen(): boolean {
    return this.isWidgetState('open');
  }

  private waitForChatLauncher(callback: (launcherBtn: HTMLElement, shadowRoot?: ShadowRoot) => void): void {
    const checkExist = setInterval(() => {
      const shadowRoot = this.getShadowRoot();
      const widget = shadowRoot?.querySelector('#bc-widget');
      const launcherBtn = shadowRoot?.querySelector('#bc-launcher-btn');

      if (widget instanceof HTMLElement && launcherBtn instanceof HTMLElement) {
        clearInterval(checkExist);
        callback(launcherBtn, shadowRoot);
        return;
      }

      const nightTimeLauncher = document.querySelector('#feedback-form-widget-icon');
      const nightTimePopup = document.querySelector('#widget_popup_iframe_parent_element');

      if (nightTimeLauncher instanceof HTMLElement && nightTimePopup instanceof HTMLElement) {
        clearInterval(checkExist);
        callback(nightTimeLauncher);
        return;
      }
    }, 500);
  }

  private handleLauncherClick = (): void => {
    setTimeout(() => {
      const isDayTimeChatOpen = this.isBoldDeskOpen();
      const isNightTimeChatOpen = this.isNightTimeWidgetOpen();

      if (isDayTimeChatOpen || isNightTimeChatOpen) {
        this.startChatHeartbeat();
      } else {
        this.stopChatHeartbeat();
      }
    }, 500);
  };

  private isNightTimeWidgetOpen(): boolean {
    const nightTimePopup = document.querySelector('#widget_popup_iframe_parent_element');
    if (nightTimePopup instanceof HTMLElement) {
      return nightTimePopup.style.display === 'block';
    }
    return false;
  }

  private observeWidgetState(): void {
    const nightTimeLauncher = document.querySelector('#feedback-form-widget-icon');
    const nightTimePopup = document.querySelector('#widget_popup_iframe_parent_element');

    if (!(nightTimeLauncher instanceof HTMLElement) || !(nightTimePopup instanceof HTMLElement)) {
      return;
    }

    const observerCallback = (mutations: MutationRecord[]) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          this.handleLauncherClick();
        }
      });
    };

    const observerOptions = { attributes: true, attributeFilter: ['style'] };
    const launcherObserver = new MutationObserver(observerCallback);
    const popupObserver = new MutationObserver(observerCallback);

    launcherObserver.observe(nightTimeLauncher, observerOptions);
    popupObserver.observe(nightTimePopup, observerOptions);
  }

  private monitorChatState(launcherBtn: HTMLElement): void {
    launcherBtn.removeEventListener('click', this.handleLauncherClick);
    launcherBtn.addEventListener('click', this.handleLauncherClick);
  }

  private monitorDaytimeInputActivity(): void {
    const inputField = this.getWidgetElement()?.querySelector('input');
    if (inputField instanceof HTMLInputElement) {
      inputField.addEventListener('input', () => {
        this.lastTypingTime = Date.now();
      });
    }
  }

  private enforceDaytimeLogout(): void {
    setInterval(() => {
      if (this.lastTypingTime && Date.now() - this.lastTypingTime > 5 * 60 * 1000) {
        this.authService.logout(undefined, 'bolddeskDaytimeInactivity');
      }
    }, 60 * 1000); // Check every minute
  }

  private enforceNighttimeSessionLimit(): void {
    if (!this.nighttimeSessionStartTime) {
      this.nighttimeSessionStartTime = Date.now();
    }

    const elapsedTime = Date.now() - this.nighttimeSessionStartTime;

    if (elapsedTime > this.maxNighttimeSessionDurationMs) {
      this.authService.logout(undefined, 'bolddeskNighttimeLimit');
      this.stopChatHeartbeat();
    }
  }

  private startChatHeartbeat(): void {
    if (this.heartbeatInterval || !this.authService.isAuthorized()) return;

    const isDaytime = this.isDaytime();

    if (isDaytime) {
      this.monitorDaytimeInputActivity();
      this.enforceDaytimeLogout();
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.authService.isAuthorized()) {
        if (isDaytime) {
          if (this.lastTypingTime && Date.now() - this.lastTypingTime <= this.CHAT_HEARTBEAT_INTERVAL_MS) {
            this.sessionService
              .touch()
              .pipe(take(1))
              .subscribe(() => {
                this.idleService.resetTimer(true);
              });
          }
        } else {
          this.sessionService
            .touch()
            .pipe(take(1))
            .subscribe(() => {
              this.idleService.resetTimer(true);
            });
          this.enforceNighttimeSessionLimit();
        }
      } else {
        this.stopChatHeartbeat();
      }
    }, this.CHAT_HEARTBEAT_INTERVAL_MS);
  }

  private isDaytime(): boolean {
    const storedDate = sessionStorage.getItem('serverDate');
    const date = storedDate ? new Date(storedDate) : new Date();
    const ltHour = this.convertToLithuaniaTime(date).getHours();
    return ltHour >= 8 && ltHour < 22; // Daytime is between 8:00 and 22:00 LT
  }

  private stopChatHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  ngOnDestroy(): void {
    this.stopChatHeartbeat();
    if (this.launcherBtn) {
      this.launcherBtn.removeEventListener('click', this.handleLauncherClick);
      this.launcherBtn = null;
    }
    this.lastTypingTime = null;
    this.nighttimeSessionStartTime = null;
  }
}
